import type { CartBreakdown, CartPayment } from '@/types/booking';

/**
 * Whether a pay-in-person acceptance must collect a card first: either the shop
 * requires a card for in-person payments, or the cart can only be reserved (not
 * booked with no payment). Shared verbatim by booking (`useBookingPayment`) and
 * standby (`useStandbyCheckout`) so the rule can't drift between them.
 */
export const isCardRequiredForInPersonPayment = ({
  requestCardForInPersonPayments,
  canBookNoPay,
  canReserve,
}: {
  requestCardForInPersonPayments: boolean;
  canBookNoPay: boolean;
  canReserve: boolean;
}): boolean => requestCardForInPersonPayments || (!canBookNoPay && canReserve);

/**
 * Taxes & fees row amount — the fee (only when it applies to the selected
 * method) plus every tax line — or `null` when there is nothing to show.
 * Extracted from `ConfirmBookingSummary` (SQR-14766) so the standby order
 * breakdown renders the identical row without re-deriving the math.
 */
export const getTaxesAndFeesAmount = (
  breakdown: Pick<CartBreakdown, 'taxes'>,
  feeAmount: number,
): number | null => {
  if (feeAmount <= 0 && breakdown.taxes.length === 0) return null;
  return breakdown.taxes.reduce((acc, tax) => acc + tax.amount, feeAmount);
};

/**
 * Promo/adjustment discount row amount (negative cents), or `null` when no
 * promo is applied. `promoAmount` is truthy-gated (not `> 0`) to match the
 * original component's behaviour for any non-zero value.
 */
export const getDiscountAmount = (
  breakdown: Pick<CartBreakdown, 'promoAmount'>,
): number | null => (breakdown.promoAmount ? -breakdown.promoAmount : null);

/**
 * Gift-card redemption row amount (negative cents), or `null` when no gift
 * card payment is on the cart.
 */
export const getGiftCardAmount = (
  payments?: readonly Pick<CartPayment, 'type' | 'amount'>[],
): number | null => {
  const giftCardPayment = payments?.find(
    (payment) => payment.type === 'gift_card',
  );
  return giftCardPayment ? -(giftCardPayment.amount || 0) : null;
};

/**
 * Sale-side promo/discount row amount (negative cents), or `null` when none
 * applied. A `CustomerSaleResponse` has no non-optional `promoAmount` the way
 * a `CartBreakdown` does, so this checks its breakdown first, then falls back
 * to a `type: 'promo'` adjustment — the same signal `calculatePromoAmount`
 * (AppointmentDetailContentWrapper) uses for the same response shape.
 */
export const getSaleDiscountAmount = (
  breakdown?: { promoAmount?: number },
  adjustments?: readonly { type: string; amount: number }[],
): number | null => {
  if (breakdown?.promoAmount) return -breakdown.promoAmount;
  const promoAdjustment = adjustments?.find((adj) => adj.type === 'promo');
  return promoAdjustment ? -Math.abs(promoAdjustment.amount) : null;
};
